import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonText,
  Heading,
  Icon,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ScrollView,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { X } from 'lucide-react-native';

import HtmlText from '@/common/components/HtmlText';

import type { ThumbsDownCheckStatus, ThumbsDownCheckView } from './thumbsDownCheckView';

export type ThumbsDownCheckModalProps = {
  visible: boolean;
  studentLabel: string;
  subline: string;
  status: ThumbsDownCheckStatus;
  check: ThumbsDownCheckView | null;
  onClose: () => void;
  onRetry?: () => void;
};

/**
 * Props-only modal for one thumbs-down check (draft + tutor comment).
 */
export function ThumbsDownCheckModal({
  visible,
  studentLabel,
  subline,
  status,
  check,
  onClose,
  onRetry,
}: ThumbsDownCheckModalProps) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={visible} onClose={onClose} size="lg">
      <ModalBackdrop />
      <ModalContent borderRadius="$xl" softShadow="4" testID="thumbs-down-check-modal">
        <ModalHeader
          borderBottomWidth={1}
          borderBottomColor="$borderLight"
          py="$4"
          justifyContent="space-between"
          alignItems="center"
        >
          <Heading size="md">{studentLabel}</Heading>
          <ModalCloseButton p="$2" m="-$2" onPress={onClose} testID="thumbs-down-check-modal-close">
            <Icon as={X} size="sm" color="$textSubtle" />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody py="$4">
          {subline ? (
            <Text size="sm" color="$textSubtle" mb="$4" testID="thumbs-down-check-modal-subline">
              {subline}
            </Text>
          ) : null}
          {status === 'loading' && (
            <VStack space="md" alignItems="center" py="$6" testID="thumbs-down-check-modal-loading">
              <Spinner size="large" />
              <Text size="sm" color="$textSubtle">
                {t('admin.analytics.feedbackHelpfulness.checkModal.loading')}
              </Text>
            </VStack>
          )}
          {status === 'error' && (
            <VStack space="md" testID="thumbs-down-check-modal-error">
              <Text size="sm" color="$statusError">
                {t('admin.analytics.feedbackHelpfulness.checkModal.error')}
              </Text>
              <Button size="sm" variant="outline" onPress={onRetry} testID="thumbs-down-check-modal-retry">
                <ButtonText>{t('admin.analytics.feedbackHelpfulness.checkModal.retry')}</ButtonText>
              </Button>
            </VStack>
          )}
          {status === 'gone' && (
            <Text size="sm" color="$textSubtle" testID="thumbs-down-check-modal-gone">
              {t('admin.analytics.feedbackHelpfulness.checkModal.gone')}
            </Text>
          )}
          {status === 'ready' && check && (
            <VStack space="lg" testID="thumbs-down-check-modal-ready">
              <VStack space="xs">
                <Heading size="sm">{t('admin.analytics.feedbackHelpfulness.checkModal.reasonHeading')}</Heading>
                <Text size="sm" color="$textBody" testID="thumbs-down-check-modal-reason">
                  {check.reason}
                </Text>
              </VStack>
              <VStack space="xs">
                <Heading size="sm">{t('admin.analytics.feedbackHelpfulness.checkModal.draftHeading')}</Heading>
                {check.draft ? (
                  <ScrollView
                    maxHeight={320}
                    borderWidth={1}
                    borderColor="$borderLight"
                    borderRadius="$md"
                    p="$3"
                    testID="thumbs-down-check-modal-draft-scroll"
                  >
                    <HtmlText
                      content={check.draft}
                      applySubmissionFormatting
                      wrapPlainTextInParagraph={false}
                      tagsStyles={{ h1: { marginTop: 0 }, h2: { marginTop: 0 } }}
                      fontSize={14}
                      testID="thumbs-down-check-modal-draft"
                    />
                  </ScrollView>
                ) : (
                  <Text size="sm" color="$textSubtle" testID="thumbs-down-check-modal-draft-missing">
                    {t('admin.analytics.feedbackHelpfulness.checkModal.draftNotKept')}
                  </Text>
                )}
              </VStack>
              <VStack space="xs">
                <Heading size="sm">{t('admin.analytics.feedbackHelpfulness.checkModal.commentHeading')}</Heading>
                {check.observation || check.nextStep ? (
                  <VStack space="sm">
                    {!!check.observation && (
                      <Text size="sm" color="$textBody" testID="thumbs-down-check-modal-observation">
                        {check.observation}
                      </Text>
                    )}
                    {!!check.nextStep && (
                      <VStack space="xs">
                        <Text size="xs" color="$textSubtle" fontFamily="$heading">
                          {t('admin.analytics.feedbackHelpfulness.checkModal.nextStepLabel')}
                        </Text>
                        <Text size="sm" color="$textBody" testID="thumbs-down-check-modal-next-step">
                          {check.nextStep}
                        </Text>
                      </VStack>
                    )}
                  </VStack>
                ) : (
                  <Text size="sm" color="$textSubtle" testID="thumbs-down-check-modal-comment-missing">
                    {t('admin.analytics.feedbackHelpfulness.checkModal.commentNotKept')}
                  </Text>
                )}
              </VStack>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter borderTopWidth={1} borderTopColor="$borderLight" py="$3">
          <Button variant="outline" onPress={onClose} testID="thumbs-down-check-modal-footer-close">
            <ButtonText>{t('admin.analytics.feedbackHelpfulness.checkModal.close')}</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
